// AOS · init on DOMContentLoaded so animations start as soon as HTML parses
// (NOT window.load — that waits for every image, holds all [data-aos]
// elements at opacity:0 until images finish, looks like a 5-15s blank page).
// AOS.refresh() on window.load catches any late-loaded images that changed layout.
(function initAOS() {
  function start() {
    if (typeof AOS === "undefined") return;
    AOS.init({
      duration: 500,
      easing: "ease-out-quart",
      once: true,
      mirror: false,
      offset: 100,
      anchorPlacement: "top-bottom",
      startEvent: "DOMContentLoaded",
      disable: function () {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      },
    });
    window.addEventListener("load", () => AOS.refresh());
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

// ─────────────────────────────────────────────────────────────────────────
//  Sticky header — transparent over hero, solid brown when scrolled.
//  Toggles data-scrolled="true|false" on #site-header. Tailwind variants
//  hook into the attribute: data-[scrolled=true]:bg-primary-dark
// ─────────────────────────────────────────────────────────────────────────
(function () {
  const header = document.getElementById("site-header");
  if (!header) return;
  const threshold = 50; // px from top before solid bg kicks in
  const onScroll = () => {
    header.setAttribute("data-scrolled", window.scrollY > threshold ? "true" : "false");
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll(); // initial state in case page loads scrolled
})();

// ─────────────────────────────────────────────────────────────────────────
//  Floating sticky CTA bar (#sticky-cta)
//  Always shows on page load. Close button hides for the current view only.
//  Live countdown reads data-offer-end (ISO date from $offer_end_date config),
//  ticks every second, and auto-hides the bar when the timer hits 0.
//  Note: PHP already gates render — if offer is expired on page load, the
//  aside isn't in the DOM at all. This JS handles expiry while a tab is open.
// ─────────────────────────────────────────────────────────────────────────
(function () {
  const bar = document.getElementById("sticky-cta");
  if (!bar) return;

  // Close buttons
  bar.querySelectorAll(".sticky-cta-close").forEach((btn) => {
    btn.addEventListener("click", () => {
      bar.style.display = "none";
    });
  });

  // Live countdown
  const endIso = bar.getAttribute("data-offer-end");
  if (!endIso) return;
  const endTime = new Date(endIso).getTime();
  if (isNaN(endTime)) return;

  const d = bar.querySelectorAll('[data-cd="d"]');
  const h = bar.querySelectorAll('[data-cd="h"]');
  const m = bar.querySelectorAll('[data-cd="m"]');
  const s = bar.querySelectorAll('[data-cd="s"]');
  const mobileD = bar.querySelector('[data-cd-mobile="d"]');

  let timer;
  function tick() {
    const ms = endTime - Date.now();
    if (ms <= 0) {
      bar.style.display = "none";
      clearInterval(timer);
      return;
    }
    const total = Math.floor(ms / 1000);
    const dd = Math.floor(total / 86400);
    const hh = Math.floor((total % 86400) / 3600);
    const mm = Math.floor((total % 3600) / 60);
    const ss = total % 60;

    d.forEach((el) => (el.textContent = dd + "d"));
    h.forEach((el) => (el.textContent = hh + "h"));
    m.forEach((el) => (el.textContent = mm + "m"));
    s.forEach((el) => (el.textContent = ss + "s"));

    if (mobileD) {
      // Mobile shows the largest non-zero unit only — concise, scannable
      if (dd > 0)      mobileD.textContent = "Ends in " + dd + "d";
      else if (hh > 0) mobileD.textContent = "Ends in " + hh + "h";
      else if (mm > 0) mobileD.textContent = "Ends in " + mm + "m";
      else             mobileD.textContent = "Ends in " + ss + "s";
    }
  }
  tick();
  timer = setInterval(tick, 1000);
})();

// ─────────────────────────────────────────────────────────────────────────
//  Multi-step lead form (#lead-form form)
//  ─ Three steps inside one <form>. Each step is a <fieldset data-step="N">.
//  ─ Next button validates the current step's fields before advancing.
//  ─ Back button moves backward without validating.
//  ─ Progress dots track current step (data-active="true|false").
//  ─ Submit only fires on the last step.
// ─────────────────────────────────────────────────────────────────────────
(function () {
  const form = document.querySelector("#lead-form form");
  if (!form) return;
  const steps = Array.from(form.querySelectorAll("[data-step]"));
  if (!steps.length) return;
  const total = steps.length;
  const dots = form.querySelectorAll("[data-form-dot]");
  const backBtn = form.querySelector("[data-form-back]");
  const nextBtn = form.querySelector("[data-form-next]");
  const submitBtn = form.querySelector("[data-form-submit]");
  const progressbar = form.querySelector('[role="progressbar"]');

  function setVisible(el, visible) {
    if (!el) return;
    // Inline style beats any author CSS — bulletproof toggle regardless
    // of what class-based display utilities are on the element.
    el.style.display = visible ? "" : "none";
    if (visible) el.removeAttribute("hidden");
    else el.setAttribute("hidden", "");
  }

  function showStep(n, scroll) {
    n = Math.max(1, Math.min(total, n));
    form.dataset.currentStep = String(n);
    steps.forEach((f) => setVisible(f, +f.dataset.step === n));
    dots.forEach((d) => (d.dataset.active = String(+d.dataset.formDot === n)));
    setVisible(backBtn, n !== 1);
    setVisible(nextBtn, n !== total);
    setVisible(submitBtn, n === total);
    if (progressbar) progressbar.setAttribute("aria-valuenow", String(n));
    // Only scroll when user clicks Next/Back — never on initial page-load init.
    if (scroll) form.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function validateStep(n) {
    const step = form.querySelector(`[data-step="${n}"]`);
    if (!step) return true;
    const fields = step.querySelectorAll("input[name], select[name], textarea[name]");
    let firstInvalid = null;
    fields.forEach((f) => {
      if (!f.checkValidity() && !firstInvalid) firstInvalid = f;
    });
    if (firstInvalid) {
      firstInvalid.reportValidity();
      firstInvalid.focus({ preventScroll: false });
      return false;
    }
    return true;
  }

  nextBtn?.addEventListener("click", () => {
    const cur = +form.dataset.currentStep || 1;
    if (validateStep(cur)) showStep(cur + 1, true);
  });

  backBtn?.addEventListener("click", () => {
    const cur = +form.dataset.currentStep || 1;
    showStep(cur - 1, true);
  });

  // Final submit guard — only allow on last step, validate all required fields with names
  // and route to Stripe Checkout instead of the legacy form.php POST.
  //
  // Flow:
  //   1. User clicks "Pay $6" (Step 3 submit button)
  //   2. Browser-level validity check (HTML5 required/email/pattern)
  //   3. POST lead JSON → /partials/checkout.php (validates + creates Stripe session)
  //   4. Server returns {url: 'https://checkout.stripe.com/...'}
  //   5. Browser navigates to that URL — Stripe handles card collection
  //   6. After payment, Stripe redirects to /thankyou.php?session_id=...
  //      thankyou.php verifies payment, sends admin email, fires Meta CAPI Purchase.
  //
  // If checkout.php fails (network, validation, Stripe error), we surface the
  // error inline via [data-checkout-error] and re-enable the button.
  const checkoutEndpoint = form.dataset.checkoutEndpoint
    || (form.getAttribute("action") || "").replace(/form\.php(?:\?.*)?$/, "checkout.php")
    || "partials/checkout.php";
  const errorBox    = form.querySelector("[data-checkout-error]");
  const submitLabel = submitBtn?.querySelector("[data-checkout-label]");
  const submitSpin  = submitBtn?.querySelector("[data-checkout-spinner]");

  function showCheckoutError(msg) {
    if (!errorBox) {
      alert(msg);
      return;
    }
    errorBox.textContent = msg;
    errorBox.classList.remove("hidden");
  }
  function clearCheckoutError() {
    if (errorBox) {
      errorBox.textContent = "";
      errorBox.classList.add("hidden");
    }
  }
  function setSubmitting(isSubmitting) {
    if (!submitBtn) return;
    submitBtn.disabled = isSubmitting;
    if (submitLabel) submitLabel.textContent = isSubmitting ? "Redirecting to secure checkout…" : "Pay $6 & Claim My 6 Weeks";
    if (submitSpin)  submitSpin.hidden = !isSubmitting;
  }

  function collectLeadData() {
    const fd = new FormData(form);
    // Convert FormData to a plain object — JSON serializes cleaner than form-encoded
    const data = {};
    fd.forEach((v, k) => { data[k] = v; });
    // 'token' (reCAPTCHA) is auto-populated by footer.php's grecaptcha.execute
    return data;
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const cur = +form.dataset.currentStep || 1;
    if (cur !== total) return;          // safety — shouldn't happen
    if (!validateStep(cur)) return;     // HTML5 validity check
    clearCheckoutError();
    setSubmitting(true);

    try {
      const res = await fetch(checkoutEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(collectLeadData()),
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Checkout failed (HTTP ${res.status})`);
      }
      if (!data.url) {
        throw new Error("Checkout response missing Stripe URL");
      }
      // Hand off to Stripe — full navigation, NOT fetch
      window.location.assign(data.url);
    } catch (err) {
      console.error("Stripe checkout error:", err);
      showCheckoutError(err.message || "Couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  });

  // Initial step state without scrolling — page loads at top, not on the form.
  showStep(1, false);
})();

// ─────────────────────────────────────────────────────────────────────────
//  YouTube popup (#video-modal)
//  Triggered by [data-video-open] buttons (Section 3 play button).
//  iframe.src is set each open so the video restarts from 00:00 every time;
//  it's cleared on close so YouTube stops playing in the background.
//  ESC + backdrop click close. Body scroll locks while open.
// ─────────────────────────────────────────────────────────────────────────
(function () {
  const modal = document.getElementById("video-modal");
  if (!modal) return;
  const iframe = document.getElementById("video-iframe");
  const opens  = document.querySelectorAll("[data-video-open]");
  const closes = modal.querySelectorAll("[data-video-close]");

  // YouTube embed URL — autoplay=1 + playsinline=1 + modestbranding=1.
  // Setting src each time ensures playback starts from 0 on every open.
  const VIDEO_SRC =
    "https://www.youtube.com/embed/NpEaa2P7qZI?autoplay=1&rel=0&modestbranding=1&playsinline=1";

  function openModal() {
    iframe.src = VIDEO_SRC;
    modal.classList.remove("hidden");
    modal.classList.add("flex");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
  }

  function closeModal() {
    iframe.src = ""; // stop playback + reset to 00:00 next time
    modal.classList.add("hidden");
    modal.classList.remove("flex");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
  }

  opens.forEach((b) => b.addEventListener("click", openModal));
  closes.forEach((b) => b.addEventListener("click", closeModal));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modal.classList.contains("hidden")) closeModal();
  });
})();

// ─────────────────────────────────────────────────────────────────────────
//  FAQ accordion (Section 10 · #faqs)
//  Each .faq-trigger button toggles its own aria-expanded attribute. The
//  CSS does the grid-template-rows transition automatically — JS only flips
//  the boolean. Opening one item does NOT close others (per the design).
// ─────────────────────────────────────────────────────────────────────────
(function () {
  const triggers = document.querySelectorAll(".faq-trigger");
  if (!triggers.length) return;
  triggers.forEach((btn) => {
    btn.addEventListener("click", () => {
      const expanded = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", String(!expanded));
    });
  });
})();

document.querySelectorAll('a[href="#"]').forEach((link) => {
  link.addEventListener("click", (e) => e.preventDefault());
});

// Smooth scroll for anchor links without updating the URL hash
// Select all anchor links that start with # but are not just '#'
document.querySelectorAll('a[href^="#"]:not([href="#"])').forEach((link) => {
  link.addEventListener("click", function (e) {
    const targetId = this.getAttribute("href").slice(1);
    const target = document.getElementById(targetId);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
      // Do not update the URL hash
      // Optionally, you can manage focus for accessibility:
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }
  });
});

